import { ROUTES } from '@/types/common';
import * as NavigateTypes from '@/types/navigate';
import useNavigator from './useNavigator';

/**
 * webview wrapper options
 * safeAreaView / bottomSafeAreaView / keyboardAvoidingView
 * @param webViewType DoctornowWebViewType
 * @default DoctornowWebViewType.BottomSafeAreaView
 *
 * webview options
 * @param scrollEnabled boolean
 * @default true
 *
 * @param bounces boolean
 * @default true
 *
 * @param keyboardDisplayRequiresUserAction boolean
 * @default true
 *
 * @param hideKeyboardAccessoryView boolean
 * @default false
 *
 * bottomSheet options
 * @param hasTelemedicineBottomSheet boolean
 * @default false
 */
const useNavigate = () => {
  const navigator = useNavigator();

  const navigateToDietInjection = () => {
    return navigator.pushScreen({
      url: ROUTES.FACE_TO_FACE.PREFERENCE_STEP.path,
    });
  };

  const navigateToFindPharmacy = () => {
    return navigator.pushScreen({
      url: ROUTES.PHARMACIES.PHARMACIES_FIND.pathName,
    });
  };

  const navigateToEmergencyContraceptive = ({ categoryId, type }: NavigateTypes.EmergencyContraceptiveScreenProps) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.DOCTOR.F2F_LIST.appScreenName,
      params: { categoryId, type },
    });
  };

  const navigateToQnaDetail = ({ questionPid, ...params }: NavigateTypes.QnaDetailScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.QNA.DETAIL.path}/${questionPid}`,
      params,
    });
  };

  const navigateToNps = () => {
    return navigator.pushScreen({ url: ROUTES.QNA.NPS.path });
  };

  const navigateToHospitalSearch = (params: NavigateTypes.HospitalSearchScreenProps) => {
    return navigator.pushScreen({
      url: ROUTES.HOSPITAL.SEARCH.path,
      params,
    });
  };

  const navigateToFaceToFaceDiagnosisMap = (
    params:
      | NavigateTypes.FaceToFaceDiagnosisMapBySemaglutideScreenProps
      | NavigateTypes.FaceToFaceDiagnosisMapByLiraglutideScreenProps
  ) => {
    return navigator.pushScreen({
      url: ROUTES.FACE_TO_FACE.DIAGNOSIS_MAP.path,
      params,
    });
  };

  const navigateToFaceToFaceDoctorList = (
    params:
      | NavigateTypes.FaceToFaceDiagnosisMapByOralDietMedicineScreenProps
      | NavigateTypes.FaceToFaceDiagnosisMapByOtherScreenProps
  ) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.DOCTOR.F2F_LIST.appScreenName,
      params,
    });
  };

  const navigateToMagazineTagList = ({ tagName }: NavigateTypes.MagazineTagListScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.MAGAZINE.TAG.path}?tag=${tagName}`,
    });
  };

  const navigateToHospitalDetail = ({ hospitalPid }: NavigateTypes.HospitalDetailScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.HOSPITAL.DETAIL.path}/${hospitalPid}`,
    });
  };

  const navigateToMagazineDetail = ({ magazinePid }: NavigateTypes.MagazineDetailScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.MAGAZINE.DETAIL.path}/${magazinePid}`,
    });
  };

  const navigateToQnaDetailWithJumpToQnaTab = ({ questionPid, ...params }: NavigateTypes.QnaDetailScreenProps) => {
    return navigator.jumpToScreen({
      tabName: 'QuickAnswerTab',
      url: `${ROUTES.QNA.DETAIL.path}/${questionPid}`,
      params,
    });
  };

  const navigateHomeWithJumpToHomeTab = (params: NavigateTypes.jumpToHomeScreenWithHomeTabProps) => {
    return navigator.jumpToAppScreen({
      tabName: 'HomeTab',
      screenName: ROUTES.HOME.appScreenName,
      params,
    });
  };

  const navigateToTreatmentRequest = (params: NavigateTypes.treatmentRequestAppScreenProps) => {
    return navigator.pushAppScreen({
      screenName: ROUTES.TREATMENT_REQUEST.TREATMENT_REQUEST.appScreenName,
      params,
    });
  };

  const navigateToCategorySelect = () => {
    return navigator.navigateAppScreen({
      screenName: 'CategorySelectScreen',
    });
  };

  const navigateToFamilyRegister = (params: NavigateTypes.FamilyRegisterWebViewScreenProps) => {
    return navigator.pushScreen({
      url: ROUTES.FAMILY.REGISTER.path,
      params: {
        ...params,
        webViewType: NavigateTypes.DoctornowWebViewType.KeyboardAvoidingView,
        keyboardDisplayRequiresUserAction: false,
        hideKeyboardAccessoryView: true,
      },
    });
  };

  const navigateToMedicineDictionaryDetail = ({
    medicineDictionaryPid,
    keyword,
  }: NavigateTypes.MedicineDictionaryDetailScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.MEDICINE_DICTIONARY.DETAIL.path}/${medicineDictionaryPid}/${encodeURIComponent(keyword)}`,
    });
  };

  const navigateToDiseaseEncyclopediaDetail = ({
    diseaseEncyclopediaPid,
    keyword,
  }: NavigateTypes.DiseaseEncyclopediaDetailScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.DISEASE_ENCYCLOPEDIA.DETAIL.path}/${diseaseEncyclopediaPid}/${encodeURIComponent(keyword)}`,
    });
  };

  const navigateToMagazineAuthorDetail = ({ authorPublicId }: NavigateTypes.MagazineAuthorDetailScreenProps) => {
    return navigator.pushScreen({
      url: `${ROUTES.AUTHOR.DETAIL.path}/${authorPublicId}`,
    });
  };

  return {
    navigateToDietInjection,
    navigateToFindPharmacy,
    navigateToEmergencyContraceptive,
    navigateToQnaDetail,
    navigateToNps,
    navigateToHospitalSearch,
    navigateToFaceToFaceDiagnosisMap,
    navigateToFaceToFaceDoctorList,
    navigateToMagazineTagList,
    navigateToHospitalDetail,
    navigateToMagazineDetail,
    navigateToQnaDetailWithJumpToQnaTab,
    navigateHomeWithJumpToHomeTab,
    navigateToTreatmentRequest,
    navigateToCategorySelect,
    navigateToFamilyRegister,
    navigateToMedicineDictionaryDetail,
    navigateToMagazineAuthorDetail,
    navigateToDiseaseEncyclopediaDetail,
  };
};

export default useNavigate;
